import { Activity, ArrowRight, CheckCircle2, FileSearch, Loader2, PlusCircle, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ReviewTable } from '../components/ReviewTable';
import { SEV_DOT } from '../components/ui/Badges';
import { buttonClasses } from '../components/ui/Button';
import { Tally } from '../components/ui/Chalk';
import type { Chalk } from '../lib/chalk';
import { CHALK } from '../lib/chalk';
import { cn } from '../lib/cn';
import { EmptyState, ErrorState, PageHeader, Skeleton } from '../components/ui/Feedback';
import { useStats } from '../hooks/queries';
import { SEVERITY_LABEL } from '../lib/format';
import { SEVERITIES } from '../types/api';

function StatCard({ label, value, icon, hint, color }: { label: string; value: number; icon: ReactNode; hint?: string; color: Chalk }) {
  return (
    <div className="card relative overflow-hidden p-5">
      <span className={cn('absolute inset-x-0 top-0 h-1', CHALK[color].bg)} aria-hidden />
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-muted">{label}</p>
        <span className={cn('rounded-full border border-dashed p-1.5', CHALK[color].border, CHALK[color].text)}>{icon}</span>
      </div>
      <p className={cn('mt-3 text-3xl font-semibold tabular-nums', CHALK[color].text)}>{value.toLocaleString()}</p>
      {hint && <p className="mt-1 text-xs text-faint">{hint}</p>}
    </div>
  );
}

const SEV_STROKE = {
  critical: 'rgb(var(--sev-critical))',
  high: 'rgb(var(--sev-high))',
  medium: 'rgb(var(--sev-medium))',
  low: 'rgb(var(--sev-low))',
  info: 'rgb(var(--sev-info))',
} as const;

const NewReviewButton = () => (
  <Link to="/app/new" className={buttonClasses('primary')}>
    <PlusCircle className="h-4 w-4" aria-hidden />
    New review
  </Link>
);

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useStats();

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="mt-6 h-64" />
      </div>
    );
  }
  if (error || !data) return <ErrorState title="Could not load dashboard" error={error} onRetry={() => void refetch()} />;

  if (data.totalReviews === 0) {
    return (
      <div>
        <PageHeader title="Dashboard" />
        <div className="card">
          <EmptyState
            icon={<FileSearch className="h-7 w-7" />}
            color="yellow"
            title="No reviews yet"
            description="Paste a GitHub Pull Request URL to get an AI review of potential bugs, security concerns and edge cases. No credentials? Try one of the clearly-labelled sample PRs."
            action={<NewReviewButton />}
          />
        </div>
      </div>
    );
  }

  const inProgress = data.byStatus.queued + data.byStatus.processing;
  const maxSev = Math.max(1, ...SEVERITIES.map((s) => data.findingsBySeverity[s]));

  return (
    <div>
      <PageHeader title="Dashboard" color="yellow" description="Everything here is counted from the reviews you have stored." actions={<NewReviewButton />} />

      {data.demoReviews > 0 && (
        <p className="mb-5 text-sm text-muted">
          {data.demoReviews === data.totalReviews ? 'All' : `${data.demoReviews} of ${data.totalReviews}`} reviews below are{' '}
          <span className="font-medium text-chalk-yellow">demo data</span> from sample PRs and are included in these totals.
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard color="blue" label="Total reviews" value={data.totalReviews} icon={<Activity className="h-4 w-4" />} />
        <StatCard color="green" label="Completed" value={data.byStatus.completed} icon={<CheckCircle2 className="h-4 w-4" />} />
        <StatCard
          color="yellow"
          label="In progress"
          value={inProgress}
          icon={<Loader2 className={cn('h-4 w-4', inProgress > 0 && 'animate-spin')} />}
          hint={`${data.byStatus.queued} queued, ${data.byStatus.processing} processing`}
        />
        <StatCard color="pink" label="Failed" value={data.byStatus.failed} icon={<XCircle className="h-4 w-4" />} />
      </div>

      <div className="mt-6 grid gap-6">
        <section className="card p-5" aria-labelledby="sev-heading">
          <div className="flex items-baseline justify-between">
            <h2 id="sev-heading" className="text-base font-semibold">
              Findings by severity
            </h2>
            <span className="text-sm text-muted">{data.totalFindings} total</span>
          </div>
          <ul className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {SEVERITIES.map((s) => (
              <li key={s} className="rounded-lg border border-line/60 bg-canvas/40 px-4 py-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${SEV_DOT[s]}`} aria-hidden />
                    {SEVERITY_LABEL[s]}
                  </span>
                  <span className="font-semibold tabular-nums">{data.findingsBySeverity[s]}</span>
                </div>
                <div className="mt-2 min-w-0 overflow-hidden">
                  <Tally count={data.findingsBySeverity[s]} max={maxSev} color={SEV_STROKE[s]} />
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="card min-w-0" aria-labelledby="recent-heading">
          <div className="flex items-center justify-between border-b border-dashed border-line px-5 py-4">
            <h2 id="recent-heading" className="text-base font-semibold">
              Recent activity
            </h2>
            <Link to="/app/history" className="inline-flex items-center gap-1 text-sm text-chalk-blue hover:underline">
              View history <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <ReviewTable reviews={data.recent} />
        </section>
      </div>
    </div>
  );
}
